"use client";

import { useTranslations } from "next-intl";
import type { RefObject } from "react";
import { BounceLoader } from "@/components/common/bounce-loader";
import { ButtonLoadingIcon } from "@/components/common/buttons/button-loading-icon";
import { FloatingBottomBar } from "@/components/floating-bottom-bar";
import { OmnibusLowestPrice } from "@/features/diets/components/omnibus-lowest-price";
import {
  isOmnibusEnabled,
  type OmnibusPresentation,
} from "@/features/diets/helpers/omnibus";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";
import type { CartPricing } from "../../types";

// Mobile bar of the diet configuration: always the per-day price, with L under
// it when available. No delivery cost and no position total here.
export const ConfigurationBar = ({
  days,
  disabled,
  edit,
  loading,
  pricingRef,
  pricing,
  omnibus,
}: {
  days?: number;
  disabled?: boolean;
  edit: boolean;
  loading?: boolean;
  pricingRef: RefObject<HTMLDivElement | null>;
  // Diet only — `pricing.final` never covers the position's addons.
  pricing?: CartPricing;
  // Omnibus presentation of this quote (MPL-1835).
  omnibus?: OmnibusPresentation;
}) => {
  const t = useTranslations();
  const omnibusEnabled = isOmnibusEnabled();
  // Under the Omnibus flag the price is red only when L allows signalling the
  // reduction (contract pt 7).
  const discounted = omnibusEnabled
    ? Boolean(omnibus?.promotion)
    : Boolean(pricing?.discounts);
  const lowestPrice =
    omnibusEnabled && omnibus?.promotion ? omnibus.lowestPrice : undefined;

  const LoadingIcon = () =>
    disabled ? (
      <ButtonLoadingIcon className="ml-2.5 inline-block" loading={disabled} />
    ) : null;

  const dailyPriceContent = (
    <div className="flex flex-col justify-center">
      <span
        className={cn(
          "font-bold text-lg leading-6.5",
          discounted ? "text-brand-red" : "text-brand-violet",
        )}
      >
        {loading ? (
          <BounceLoader className="my-2 h-4" />
        ) : (
          pricing?.final.gross && (
            <span>
              <span className="font-bold">
                {getLocalePrice({
                  price: pricing.final.gross / (days || 1),
                })}
              </span>
              <span> {t("cart.configuration.perDay")}</span>
            </span>
          )
        )}
      </span>
      {lowestPrice !== undefined && (
        <OmnibusLowestPrice lowestPrice={lowestPrice} short />
      )}
    </div>
  );

  return (
    <FloatingBottomBar
      button={{
        disabled: disabled || loading,
        form: "configure-diet-form",
        label: edit ? (
          <>
            <span>{t("cart.configuration.save")}</span>
            <LoadingIcon />
          </>
        ) : (
          <>
            <span>{t("cart.configuration.addToCartSmall")}</span>
            <LoadingIcon />
          </>
        ),
        type: "submit",
        className: "px-6!",
      }}
      content={dailyPriceContent}
      hidingElementRef={pricingRef}
    />
  );
};
